import React from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useParcelContext } from '../../../components/ParcelProvider';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import ParcelItem from './ParcelItem';
import ParcelItemSkeleton from './ParcelItemSkeleton';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import { productImageFormatter, productSkuFormatter } from './formatters';

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const ParcelItems = () => {
  const intl = useIntl();
  const { parcel } = useParcelContext();

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.searchParcelItems(
          {
            constraints: [
              {
                key: 'parcel_ids',
                operator: 'in',
                value: [parcel?.id],
              },
            ],
          },
          requestOptions,
        ),
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const skus = data.map(item => item.sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );

          return data.reduce((acc, item) => {
            const [id] = item?.ids ?? [];
            const product = findObjectInArray(products, 'sku', item.sku);

            return [
              ...acc,
              {
                ...item,
                id,
                product,
              },
            ];
          }, []);
        },
      },
    );

  const dataSource = useDataGrid(fetchItems, {
    modules: ['dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'image',
          name: '',
          type: 'productImage',
          width: '50px',
        },
        {
          key: 'sku',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcelItems.sku',
            defaultMessage: 'SKU',
            description: 'The column name for column sku',
          }),
          type: 'productSku',
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcelItems.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'quantity',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcelItems.quantity',
            defaultMessage: 'Quantity',
            description: 'The column name for column quantity',
          }),
        },
      ],
    },
  });

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={item => <ParcelItem item={item} />}
          renderSkeletonItem={key => <ParcelItemSkeleton key={key} />}
          component={DataListContainerGrid}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{ productImage: productImageFormatter, productSku: productSkuFormatter }}
        />
      </Hidden>
    </>
  );
};

export default ParcelItems;
